import {Component, OnInit, DestroyRef} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {Title} from '@angular/platform-browser';
import {ActivatedRoute, Router} from '@angular/router';
import {DataService} from '../../../Services/data.service';
import {StorageService} from '../../../Services/storage.service';
import {BsToastService} from '../../../Services/bs-toast-service';
import {NavigationService} from '../../../Services/navigation.service';
import {v4 as uuidv4} from 'uuid';

@Component({
  selector: 'app-employee-type-form',
  templateUrl: './employee-type-form.component.html',
  styleUrls: ['./employee-type-form.component.css'],
  standalone: false
})
export class EmployeeTypeFormComponent implements OnInit {
  EmployeeTypeForm: UntypedFormGroup;

  vm: any = {
    title: 'Employee Type Form',
    item: {},
    buttonsDisabled: false,
    Preferences: [],
    mainListPage: '/payroll/employee-type-list',
    formInitiated: false,
    tiers: [],
  };

  submitted = false;
  employeeTypeId = '';
  isSaveNew = false;

  buttonConfig: any = {
    save: true,
    close: true,
    reset: true,
    delete: true,
    saveNew: true,
    saveClose: true,
    print: false,
  };

  payFrequencyOptions = [
    {value: 'weekly', label: 'Weekly (52/yr)', periods: 52},
    {value: 'biweekly', label: 'Bi-Weekly (26/yr)', periods: 26},
    {value: 'semimonthly', label: 'Semi-Monthly (24/yr)', periods: 24},
    {value: 'monthly', label: 'Monthly (12/yr)', periods: 12},
  ];

  accrualMethodOptions = [
    {value: 'per_pay_period', label: 'Per Pay Period'},
    {value: 'per_hour_worked', label: 'Per Hour Worked'},
    {value: 'front_loaded', label: 'Front Loaded'},
    {value: 'year_reset', label: 'Annual Reset'},
  ];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private localStorage: StorageService,
    private tableDataService: DataService,
    private notification: BsToastService,
    private titleService: Title,
    private formBuilder: UntypedFormBuilder,
    private nav: NavigationService,
    private destroyRef: DestroyRef,
  ) {
  }

  ngOnInit() {
    this.titleService.setTitle(this.vm.title);
    this.vm.Preferences = JSON.parse(this.localStorage.StorageGet('Preferences'));
    this.formInit();
    this.dataInit();
  }

  dataInit() {
    this.route.queryParams.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      this.employeeTypeId = params.id;
      if (this.employeeTypeId) {
        this.tableDataService.getData('pr_employee_type', this.employeeTypeId).subscribe({
          next: (result: any) => {
            this.vm.item = result;
            this.vm.formInitiated = true;
            this.setValues();
            this.loadTiers();
          },
          error: (err) => {
            console.error(err);
            this.notification.showErrorToast($localize`:Error loading employee type@@errorLoadingEmployeeType:Error loading employee type`);
          },
        });
      } else {
        this.vm.formInitiated = true;
      }
    });
  }

  loadTiers() {
    if (!this.employeeTypeId) {
      return;
    }
    this.tableDataService.getData(`pr_employee_type/${this.employeeTypeId}/tenure_tiers`).subscribe({
      next: (result: any) => {
        this.vm.tiers = Array.isArray(result) ? result : [];
      },
      error: (err) => console.error(err),
    });
  }

  get currentPeriods(): number {
    const freq = this.f['item_pay_frequency']?.value || 'biweekly';
    return this.payFrequencyOptions.find(o => o.value === freq)?.periods ?? 26;
  }

  recalcAccrualRates() {
    const periods = this.currentPeriods;
    const ptoMax = +this.f['item_pto_max_hours'].value || 0;
    const sickMax = +this.f['item_sick_max_hours'].value || 0;
    const ptoMethod = this.f['item_pto_accrual_method'].value;
    const sickMethod = this.f['item_sick_accrual_method'].value;

    if (ptoMethod === 'per_pay_period' && ptoMax > 0) {
      this.EmployeeTypeForm.patchValue({
        item_pto_accrual_rate: +(ptoMax / periods).toFixed(4),
      });
    }
    if (sickMethod === 'per_pay_period' && sickMax > 0) {
      this.EmployeeTypeForm.patchValue({
        item_sick_accrual_rate: +(sickMax / periods).toFixed(4),
      });
    }
  }

  get f() {
    return this.EmployeeTypeForm.controls;
  }

  formInit() {
    this.EmployeeTypeForm = this.formBuilder.group({
      item_name: new UntypedFormControl('', Validators.required),
      item_description: new UntypedFormControl(''),
      item_pto_max_hours: new UntypedFormControl(0),
      item_pto_accrual_rate: new UntypedFormControl(0),
      item_pto_accrual_method: new UntypedFormControl('per_pay_period'),
      item_holiday_hours: new UntypedFormControl(0),
      item_sick_max_hours: new UntypedFormControl(0),
      item_sick_accrual_rate: new UntypedFormControl(0),
      item_sick_accrual_method: new UntypedFormControl('per_pay_period'),
      item_max_carryover_hours: new UntypedFormControl(0),
      item_accrual_waiting_days: new UntypedFormControl(0),
      item_pay_frequency: new UntypedFormControl('biweekly'),
      item_active: new UntypedFormControl(true),
    });
  }

  setValues() {
    this.EmployeeTypeForm.patchValue({
      item_name: this.vm.item.name,
      item_description: this.vm.item.description,
      item_pto_max_hours: this.vm.item.pto_max_hours,
      item_pto_accrual_rate: this.vm.item.pto_accrual_rate,
      item_pto_accrual_method: this.vm.item.pto_accrual_method,
      item_holiday_hours: this.vm.item.holiday_hours,
      item_sick_max_hours: this.vm.item.sick_max_hours,
      item_sick_accrual_rate: this.vm.item.sick_accrual_rate,
      item_sick_accrual_method: this.vm.item.sick_accrual_method,
      item_max_carryover_hours: this.vm.item.max_carryover_hours,
      item_accrual_waiting_days: this.vm.item.accrual_waiting_days,
      item_pay_frequency: this.vm.item.pay_frequency || 'biweekly',
      item_active: this.vm.item.active,
    });
  }

  setupValuesForSave() {
    this.vm.item.name = this.f['item_name'].value;
    this.vm.item.description = this.f['item_description'].value;
    this.vm.item.pto_max_hours = this.f['item_pto_max_hours'].value;
    this.vm.item.pto_accrual_rate = this.f['item_pto_accrual_rate'].value;
    this.vm.item.pto_accrual_method = this.f['item_pto_accrual_method'].value;
    this.vm.item.holiday_hours = this.f['item_holiday_hours'].value;
    this.vm.item.sick_max_hours = this.f['item_sick_max_hours'].value;
    this.vm.item.sick_accrual_rate = this.f['item_sick_accrual_rate'].value;
    this.vm.item.sick_accrual_method = this.f['item_sick_accrual_method'].value;
    this.vm.item.max_carryover_hours = this.f['item_max_carryover_hours'].value;
    this.vm.item.accrual_waiting_days = this.f['item_accrual_waiting_days'].value;
    this.vm.item.pay_frequency = this.f['item_pay_frequency'].value;
    this.vm.item.active = this.f['item_active'].value;
  }

  saveClose() {
    this.onSave(true);
  }

  saveNew() {
    this.isSaveNew = true;
    this.onSave(false);
  }

  resetForm() {
    this.resetToDefaults();
  }

  onClose() {
    this.nav.backOr(this.vm.mainListPage);
  }

  deleteItem() {
    if (!this.vm.item.id) {
      return;
    }
    this.vm.buttonsDisabled = true;
    this.tableDataService.deleteDataById('pr_employee_type', this.vm.item.id).subscribe({
      next: () => {
        this.notification.showSuccessToast($localize`:Deleted@@deleted:Deleted`);
        this.onClose();
      },
      error: (err) => {
        this.vm.buttonsDisabled = false;
        console.error(err);
        this.notification.showErrorToast($localize`:Error deleting employee type@@errorDeletingEmployeeType:Error deleting employee type`);
      },
    });
  }

  private resetToDefaults() {
    this.EmployeeTypeForm.reset({
      item_name: '',
      item_description: '',
      item_pto_max_hours: 0,
      item_pto_accrual_rate: 0,
      item_pto_accrual_method: 'per_pay_period',
      item_holiday_hours: 0,
      item_sick_max_hours: 0,
      item_sick_accrual_rate: 0,
      item_sick_accrual_method: 'per_pay_period',
      item_max_carryover_hours: 0,
      item_accrual_waiting_days: 0,
      item_pay_frequency: 'biweekly',
      item_active: true,
    });
    this.vm.item = {};
    this.vm.tiers = [];
    this.submitted = false;
    this.vm.buttonsDisabled = false;
    this.employeeTypeId = '';
  }

  onSave(saveClose: boolean) {
    this.submitted = true;
    this.vm.buttonsDisabled = true;

    if (this.EmployeeTypeForm.invalid) {
      this.notification.showWarningToast($localize`:Form Errors!@@formerrors:Form Errors!`);
      this.vm.buttonsDisabled = false;
      return false;
    }

    this.setupValuesForSave();

    if (!this.vm.item.id) {
      this.vm.item.id = uuidv4();
      this.tableDataService.post('pr_employee_type', this.vm.item).subscribe({
        next: () => {
          this.notification.showSuccessToast($localize`:Saved@@saved:Saved`);
          this.vm.buttonsDisabled = false;
          this.submitted = false;
          if (saveClose) {
            this.onClose();
          } else if (this.isSaveNew) {
            this.resetToDefaults();
            this.isSaveNew = false;
          }
        },
        error: (err) => {
          this.vm.buttonsDisabled = false;
          console.error(err);
        },
      });
    } else {
      this.tableDataService.put('pr_employee_type', this.vm.item.id, this.vm.item).subscribe({
        next: () => {
          this.notification.showSuccessToast($localize`:Saved@@saved:Saved`);
          this.vm.buttonsDisabled = false;
          this.submitted = false;
          if (saveClose) {
            this.onClose();
          } else if (this.isSaveNew) {
            this.resetToDefaults();
            this.isSaveNew = false;
          }
        },
        error: (err) => {
          this.vm.buttonsDisabled = false;
          console.error(err);
        },
      });
    }
    return true;
  }

  // ── Tenure Tiers ──────────────────────────────────────────────────────────

  addTier() {
    this.vm.tiers.push({
      id: uuidv4(),
      employee_type_id: this.vm.item.id,
      min_years: 0,
      max_years: null,
      pto_accrual_rate: 0,
      sick_accrual_rate: 0,
      _isNew: true,
    });
  }

  saveTier(tier: any) {
    if (!this.vm.item.id) {
      this.notification.showWarningToast($localize`:Save the employee type first@@saveEmployeeTypeFirst:Save the employee type first`);
      return;
    }
    if (tier._isNew) {
      this.tableDataService.post(`pr_employee_type/${this.vm.item.id}/tenure_tiers`, tier).subscribe({
        next: () => {
          tier._isNew = false;
          this.notification.showSuccessToast($localize`:Tier saved@@tierSaved:Tier saved`);
        },
        error: (err) => console.error(err),
      });
    } else {
      this.tableDataService.put(`pr_employee_type/${this.vm.item.id}/tenure_tiers`, tier.id, tier).subscribe({
        next: () => this.notification.showSuccessToast($localize`:Tier saved@@tierSaved:Tier saved`),
        error: (err) => console.error(err),
      });
    }
  }

  deleteTier(tier: any, index: number) {
    if (tier._isNew) {
      this.vm.tiers.splice(index, 1);
      return;
    }
    this.tableDataService.deleteDataById(`pr_employee_type/${this.vm.item.id}/tenure_tiers`, tier.id).subscribe({
      next: () => {
        this.vm.tiers.splice(index, 1);
        this.notification.showSuccessToast($localize`:Tier deleted@@tierDeleted:Tier deleted`);
      },
      error: (err) => console.error(err),
    });
  }
}
